import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';

/**
 * The help screen is a collection of components where each topic is its own component.
 * This is the parent component which holds all components under the help-screen folder, displaying one at a time.
 * All paths to images held here. Each help component receives these paths and informs this component when an image should be inflated onto the screen.
 */
@Component({
    selector: 'app-help-screen',
    templateUrl: './help-screen.component.html',
    styleUrls: ['./help-screen.component.css'],
    standalone: false
})
export class HelpScreenComponent implements OnInit {

  // screen width
  screenWidth: number = window.innerWidth;

  // links and link types
  website: string = "";
  linkType: string = "";

  // PSMFC email
  email: string = "";

  // navigation
  prevScreen: string = "";
  prevHeader: string = "";
  currHeader: string = "";
  currIndex: number = 0;
  listItems: string[] = [""];

  // images
  imgSource: string = "";
  currImg: string = "";
  viewerOpen: boolean = false;

  // Help option images
  downloadImgs: string[] = [];
  downloadImgsInput: string[] = [];
  accountImgs: string[] = [];
  accountImgsInput: string[] = [];
  menuImgs: string[] = [];
  menuImgsInput: string[] = [];
  setsImgs: string[] = [];
  setsImgsInput: string[] = [];
  deliveryImgs: string[] = [];
  deliveryImgsInput: string[] = [];
  logbookImgs: string[] = [];
  hmsImgs: string[] = [];
  hmsImgsInput: string[] = [];
  logbookImgsInput: string[] = [];
  enableGeolocationImgs: string[] = [];
  enableGeolocationInput: string[] = [];
  logbookEditingImgs: string[] = [];
  logbookEditingInput: string[] = [];

  topBarWidth: string = 'auto';

  constructor(public router: Router) {

    // set website link and link type
    this.website = "https://fedelog.psmfc.org";
    this.linkType = "_blank";

    // set email link
    this.email = "FedElog@psmfc.org";

    // list of options at the help screen
    this.listItems = [
      "Use the Help Form",
      "Quick Guide",
      "Download the App",
      "Create an Account",
      "Main Menu",
      "Sets and Catch",
      "Current Delivery",
      "Logbook Submission",
      "HMS Logbook Data",
      "Common Questions About What Data to Enter",
      "Add Vessels or Check for updates",
      "Update your email or phone number",
      "Get Additional Help",
      "Enable Geolocation",
      "Editing and Resubmitting Logbooks"
    ];

    // get all images from the source
    this.imgSource = "../../../assets/images/";

    // get "Download the App" images
    this.downloadImgs = [
      "download_icon.png",
      "safari_download.jpg",
      "add_to_screen.jpg",
      "install_prompt.png",
      "chrome_download.jpg",
      "install_app_banner.png"
    ];
    this.downloadImgsInput = this.setImgPaths(this.downloadImgs);

    // get "Create an Account" images
    this.accountImgs = [
      "create_account_1.png",
      "create_account_2.png",
      "vessel_reg_1.jpg",
      "vessel_reg_2.jpg",
    ];
    this.accountImgsInput = this.setImgPaths(this.accountImgs);

    // get "Main Menu" images
    this.menuImgs = [
      "main_menu_mobile_sets.png",
      "main_menu_options.png"
    ];
    this.menuImgsInput = this.setImgPaths(this.menuImgs);

    // get "Sets and Catch" images
    this.setsImgs = [
      "main_menu_add_set.png",
      "set_list_1.png",
      "set_detail_1.png",
      "set_detail_2.png",
      "delete_gear_1.png",
      "delete_gear_2.png",
      "view_set_1.png",
      "delete_set_1.png"
    ];
    this.setsImgsInput = this.setImgPaths(this.setsImgs);

    // get "Current Delivery" images
    this.deliveryImgs = [
      "current_delivery.png",
      "view_delivery_1.png",
      "view_delivery_2.png"
    ];
    this.deliveryImgsInput = this.setImgPaths(this.deliveryImgs);

    // get "Logbook Submission" images
    this.logbookImgs = [
      "submit_log_1.png",
      "submit_log_2.png",
      "submit_log_3.png"
    ];
    this.logbookImgsInput = this.setImgPaths(this.logbookImgs);

    // get "HMS Logbook Data" images
    this.hmsImgs = [
      "HMSMenu.jpg",
      "HMSSet.jpg",
      "HMSCatch.jpg",
      "HMSSubmission.jpg",
      "HMSCurrentDelivery.jpg",

    ];
    this.hmsImgsInput = this.setImgPaths(this.hmsImgs);

    this.enableGeolocationImgs = [
      "android-geo3.jpg",
      "android-geo4.jpg",
      "android-geo5.jpg",
      "android-geo6.jpg",
      "android-geo7.jpg",
      "ios-geo2.jpg",
      "ios-geo5.jpg",
      "ios-geo4.jpg",
      "ios-geo7.jpg",
      "ios-geo3.jpg"
    ];
    this.enableGeolocationInput = this.setImgPaths(this.enableGeolocationImgs);

    this.logbookEditingImgs = [
      "beforeEditing.png",
      "editedDeliveryMainMenu.png",
      "editedDeliveryDeliveryList.png",
      "discardButton.png",
      "discardEdits.png",
      "submitButton.png"
    ];
    this.logbookEditingInput = this.setImgPaths(this.logbookEditingImgs);
    // set up help screen
    this.toHelpScreen();

  }

  ngOnInit(): void {
  }


  /**
   * From the menu of help options a click on one item runs this method.
   * Note we need to change styles on the top bar for some help screen titles.
   * @param helpOption Help screen to go to
   * @param index index into list of help components
   */
  helpOptionClick(helpOption: string, index: number): void {
    this.prevScreen = "help_screen";
    this.prevHeader = "Help";
    this.currHeader = helpOption;
    this.currIndex = index;

    // top bar header width too wide
    let isMaxWidth: boolean = this.currIndex === 9 || this.currIndex === 10 || this.currIndex === 11;
    if (this.screenWidth < 464 && isMaxWidth) {
      this.topBarWidth = 'max-content';
    }
  }

  // go to previous screen
  toPrevScreen(): void {
    if (this.currIndex === -1) {
      this.router.navigate(['menu']);
    }
    else {
      this.toHelpScreen();
    }
  }

  /**
   * To the main help screen
   */ 
  toHelpScreen(): void {
    this.prevScreen = "menu";
    this.prevHeader = "Home";
    this.currHeader = "Contents";
    this.currIndex = -1;
    this.topBarWidth = "";
  }

  /**
   * Set paths of images
   */ 
  setImgPaths(imgs: string[]): string[] {

    let imgsInput: string[] = [];

    imgs.forEach((image: string) => {
      imgsInput.push(this.imgSource + image);
    });

    return imgsInput;

  }

  /**
   * Show image in full on screen when clicked
   */ 
  showImage(url: string): void {
    this.currImg = url;
    this.viewerOpen = true;
  }


  @HostListener('window:resize', ['$event'])  
  onResize() {  
    this.screenWidth = window.innerWidth;  
  }

}
